"use client";

import { useRouter } from "next/navigation";
import { type ReactNode, useCallback, useEffect, useState } from "react";

import { useUserContext } from "@/context/user.context";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import type { DataDumpResponse } from "@/types/api";
import {
  type ExtensionMessageEvents,
  ExtensionOutgoingMessageTypes,
} from "@/types/extension";
import { getExtensionParentOrigin, sendPageData } from "@/utils/extension";

export const ExtensionProvider = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const track = useTrackAnalytics();
  const { company } = useUserContext();
  const [pageData, setPageData] = useState<DataDumpResponse>(); // response from extension

  const handleMessageEvent = useCallback(
    (event: ExtensionMessageEvents) => {
      // DEVOPS2-571: only trust messages from the embedding parent (the ATS
      // page the extension's content script runs in). Drops everything else —
      // including all extension-shaped messages when the app is not framed.
      const parentOrigin = getExtensionParentOrigin();

      if (!parentOrigin || event.origin !== parentOrigin) {
        return;
      }

      const { data } = event;

      switch (data.type) {
        case ExtensionOutgoingMessageTypes.PageDataCollected: {
          if (company) {
            sendPageData({ companyId: company.id, dom: data.dom }).then(
              (data: DataDumpResponse) => {
                setPageData(data);
              },
            );
          }

          break;
        }
        case ExtensionOutgoingMessageTypes.TrackAnalytics: {
          const { eventName, properties, options } = data.payload;

          track(eventName, properties, options);

          break;
        }
      }
    },
    [company, track],
  );

  useEffect(() => {
    window.addEventListener("message", handleMessageEvent);

    return () => window.removeEventListener("message", handleMessageEvent);
  }, [handleMessageEvent]);

  useEffect(() => {
    if (pageData) {
      switch (pageData.page) {
        case "all":
          router.push("/");
          break;
        case "candidate": {
          const candidateEmails = pageData.candidateEmails;
          router.push(`/candidates/${encodeURIComponent(candidateEmails[0])}`);
          break;
        }
        case "job": {
          const screenerLocationId = pageData.screenerLocationIds[0];
          router.push(`/screener-location/${screenerLocationId}`);
          break;
        }
      }
    }
  }, [pageData, router]);

  return children;
};
